import moment, { Moment } from "moment";
import "moment/locale/es";
import { getLanguage } from "@utils/LanguageUtils";
import { i18n } from "@translations/i18n";
import { messages } from "@utils/Messages";

export enum DateTimeFormat {
  DATE_FORMAT_1 = "DD/MM/YYYY hh:mm a",
  DATE_FORMAT_2 = "D/M/YYYY",
  DATE_FORMAT_3 = "DD.MM.YYYY",
  DATE_FORMAT_4 = "YYYY-MM-DD HH:mm:00",
  DATE_FORMAT_5 = "MMMM Do YYYY",
  DATE_FORMAT_6 = "YYYY-MM-DD",
  DATE_FORMAT_7 = "DD-MMM-YYYY",
  TIME_FORMAT_1 = "h:mm a",
  DATE_TIME_FORMAT_1 = "yyyy-MM-dd'T'HH:mm:ss'Z'",
  DATE_TIME_FORMAT_2 = "YYYY-MM-DDTHH:mm:ss",
}
const getDateFromTimeStamp = (timeStamp: Date): string => {
  return moment(timeStamp).locale(getLanguage()).format("DD MMM");
};

export const getDateDataFromTimeStamp = (timeStamp: any): string => {
  return moment(timeStamp).locale(getLanguage()).format("DD.MM.YYYY");
};

export const getDateTimePickerDataFromTimeStamp = (timeStamp: any): string => {
  return moment(timeStamp)
    .locale(getLanguage())
    .format("DD.MM.YYYY HH:mm:ss a");
};

const getHourFromTimeStamp = (timeStamp: Date, meridiem: boolean): string => {
  const am = meridiem ? "a" : "";
  return moment(timeStamp)
    .locale(getLanguage())
    .format("h:mm" + am);
};

const getHourAgoTimeStamp = (timeStamp: Date): string => {
  return moment(timeStamp).locale(getLanguage()).fromNow();
};

const getCurrentDateTime = (): string => {
  return moment().locale(getLanguage()).toISOString();
};

const getModifiedDateFromTimeStamp = (timeStamp: Date) => {
  return moment(timeStamp).locale(getLanguage()).format("Do MMM");
};

const getModifiedDateTimeStamp = (timeStamp: Date): string => {
  return moment(timeStamp).locale(getLanguage()).format("DD MMM, YYYY");
};

const getModifiedDay = (timeStamp: string): string => {
  return moment(timeStamp, "DD MMM, YYYY").locale(getLanguage()).format("dddd");
};

const getDateTime = (timeStamp: Date, format: string) => {
  return moment(timeStamp).locale(getLanguage()).format(format);
};

export const isBeforeDate = (date1: string, date2: string) => {
  return moment(date1).isBefore(date2);
};

export const isOverdueDate = (date: string) => {
  return moment(date).isBefore(moment());
};

const convertDateTimeToString = (date: Date): string => {
  return moment(date).toISOString();
};

const isToday = (timeStamp: Date): string => {
  const convertedTimeStamp = moment(timeStamp, "DD/MM/YYYY");
  if (convertedTimeStamp.isSame(new Date(), "day")) {
    return i18n.t(messages.today.key);
  }
  return getDateFromTimeStamp(timeStamp);
};

export const getMomentFormat = (date: string, format: string): Moment => {
  return moment(date, format).local(getLanguage());
};

export const getDateDifferenceInDays = (
  currentDate: any,
  nextDate: any
): number => {
  return moment(nextDate).diff(moment(currentDate), "days");
};

export const getCommentHourFromTimeStamp = (
  timeStamp: Date | string,
  meridien: boolean
): string => {
  const am = meridien ? "A" : "";
  return moment(timeStamp).format("h:mm " + am);
};

export const disablePastDate = (current) => {
  return current && current.valueOf() < Date.now();
};

export const getCurrentDate = () => {
  return moment(new Date().toISOString()).format(DateTimeFormat.DATE_FORMAT_6);
};
// Returns formated current and next date along with their difference in days

export const getDateFormatedData = (currentDate: any, nextDate: any) => {
  const d1 = DateHelper.getDateTime(currentDate, DateTimeFormat.DATE_FORMAT_3);
  const d2 = DateHelper.getDateTime(nextDate, DateTimeFormat.DATE_FORMAT_3);
  const formatedCurrentDate: any = DateHelper.getMomentFormat(
    d1,
    DateTimeFormat.DATE_FORMAT_3
  );
  const formatedNextDate: any = DateHelper.getMomentFormat(
    d2,
    DateTimeFormat.DATE_FORMAT_3
  );
  const diff = DateHelper.getDateDifferenceInDays(
    formatedCurrentDate,
    formatedNextDate
  );
  return { formatedCurrentDate, formatedNextDate, diff };
};

export const convertDateMilliSecond = (
  date: string | number,
  Dateformat: string
) => {
  const ISODate = new Date(date).toString();
  return moment(ISODate).format(Dateformat);
};
export const getConvertedTimeStampDate = (timestamp: number): string => {
  // Create a new Date object using the timestamp
  const date = new Date(timestamp);
  // Get the components of the date
  const year = date.getFullYear();
  const month = ("0" + (date.getMonth() + 1)).slice(-2); // Months are zero based
  const day = ("0" + date.getDate()).slice(-2);
  let hours = date.getHours();
  const minutes = ("0" + date.getMinutes()).slice(-2);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // The hour '0' should be '12'
  const formattedHours = ("0" + hours).slice(-2);

  // Format the date as desired
  const formattedDate = `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
  return formattedDate;
};

export const getConvertedDate = (date: Date) => {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const formattedDate =
    date.getDate() + "-" + months[date.getMonth()] + "-" + date.getFullYear();
  return formattedDate;
};

export const formatdate = (dateString, dateFormat: DateTimeFormat) => {
  if (!dateString) return dateString;
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  let formattedDate = dateString;
  switch (dateFormat) {
    case DateTimeFormat.DATE_FORMAT_2:
      formattedDate = `${day}/${month}/${year}`;
      break;
  }
  if (!isNaN(formattedDate)) return "";
  return formattedDate;
};


export const formatUpdatedDate = (date) => {

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const dayName = days[date.getDay()];
  const day = String(date.getDate()).padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const timeZone = 'GMT+0530 (India Standard Time)';

  return `${dayName} ${month} ${day} ${year} ${hours}:${minutes}:${seconds} ${timeZone}`;
};


export const getCurrentTime = () => {
    const now = new Date();

    const hours = now.getHours();
    const minutes =  now.getMinutes();
    return `${hours}:${minutes}`;
}

export const DateHelper = {
  getDateFromTimeStamp,
  getHourFromTimeStamp,
  getCurrentDateTime,
  getModifiedDateFromTimeStamp,
  getModifiedDateTimeStamp,
  getModifiedDay,
  getDateTime,
  isBeforeDate,
  convertDateTimeToString,
  getHourAgoTimeStamp,
  isOverdueDate,
  isToday,
  getMomentFormat,
  getDateDifferenceInDays,
  getCommentHourFromTimeStamp,
  getDateFormatedData,
  disablePastDate,
  getCurrentDate,
  convertDateMilliSecond,
};
